/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useMemo } from 'react';
import { useSetting } from '@airasia-rn/account/hooks';
import {
  HOTEL_LISTING_FLAGS_DEFAULT,
  HOTELS_LISTING_FEATURE_FLAGS,
} from '../configs/featureFlagConstant';
import type { HotelListingFlags } from '../types';

type UseListFeatureFlagResult = HotelListingFlags & {
  isSettingsLoading: boolean;
};

export const useListFeatureFlag = () => {
  const { value, isSettingsLoading } = useSetting(HOTELS_LISTING_FEATURE_FLAGS);

  const listingFlags = useMemo<HotelListingFlags>(() => {
    if (!value) {
      return HOTEL_LISTING_FLAGS_DEFAULT;
    }

    try {
      const sanitized = value.replace(/,(\s*[}\]])/g, '$1');
      const parsedFlags = JSON.parse(sanitized);

      if (
        parsedFlags &&
        typeof parsedFlags === 'object' &&
        !Array.isArray(parsedFlags)
      ) {
        return {
          ...HOTEL_LISTING_FLAGS_DEFAULT,
          ...(parsedFlags as Partial<HotelListingFlags>),
        };
      }

      return HOTEL_LISTING_FLAGS_DEFAULT;
    } catch {
      return HOTEL_LISTING_FLAGS_DEFAULT;
    }
  }, [value]);

  return {
    ...listingFlags,
    isSettingsLoading,
  } as UseListFeatureFlagResult;
};
